// ============================================================================
// OPTIMIZED EMPLOYEES SERVICE - PERFORMANCE ENHANCED
// frontend/src/services/ptsemployees.service.ts
// ============================================================================

import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  Firestore,
  QuerySnapshot,
  DocumentData,
  DocumentSnapshot,
  Unsubscribe
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: "AIzaSyC_cwJ2lBqsDMzWmGIlcBqyBb2BqP3I6aA",
  authDomain: "face-authentication-app-44ddb.firebaseapp.com",
  projectId: "face-authentication-app-44ddb",
  storageBucket: "face-authentication-app-44ddb.firebasestorage.app",
  messagingSenderId: "541867932129",
  appId: "1:541867932129:android:ed73e3859b053c1555faa0"
};

export interface Employee {
  id: string;
  name: string;
  email?: string;
  department?: string;
  position?: string;
  designation?: string;
  image?: string;
  phone?: string;
  isActive: boolean;
  joinDate?: string;
  lastActive?: string;
  pin?: string;
  employeeNumber?: string;
}

export interface EmployeeWithLocation {
  employee: Employee;
  currentLocation?: {
    locationId: string;
    locationName: string;
    checkInTime: string;
    coordinates: { lat: number; lng: number };
  };
}

// ============================================================================
// STATIC CONFIGURATION
// ============================================================================

export const SPECIAL_DESIGNATIONS = {
  'Civil Foreman': { name: 'Civil Foreman', color: '#ff6b35', icon: '👷‍♂️' },
  'Foreman (Agriculture)': { name: 'Foreman (Agriculture)', color: '#2ecc71', icon: '🌱' },
  'Foreman (Irrigation)': { name: 'Foreman (Irrigation)', color: '#3498db', icon: '💧' },
  'Foreman (MEP Technician)': { name: 'Foreman (MEP Technician)', color: '#e74c3c', icon: '⚡' },
  'Foreman ( Landscape)': { name: 'Foreman (Landscape)', color: '#27ae60', icon: '🌳' },
  'Mason': { name: 'Mason', color: '#8e44ad', icon: '🧱' },
  'Site Civil Engineer': { name: 'Site Civil Engineer', color: '#f39c12', icon: '📐' },
  'Site Engineer': { name: 'Site Engineer', color: '#1abc9c', icon: '🏗️' },
  'Construction Manager': { name: 'Construction Manager', color: '#34495e', icon: '👔' },
  'Softscape & Irrigation Foreman': { name: 'Softscape & Irrigation Foreman', color: '#16a085', icon: '🌿' }
} as const;

const SPECIAL_DESIGNATION_KEYS = new Set(Object.keys(SPECIAL_DESIGNATIONS));

export const isSpecialDesignation = (designation?: string): boolean => {
  if (!designation) return false;
  return SPECIAL_DESIGNATION_KEYS.has(designation);
};

export const getDesignationInfo = (designation?: string) => {
  if (!designation || !isSpecialDesignation(designation)) {
    return { name: designation || 'Employee', color: '#38b2a8', icon: '👤' };
  }
  return SPECIAL_DESIGNATIONS[designation as keyof typeof SPECIAL_DESIGNATIONS];
};

export type EmployeesCallback = (employees: Employee[]) => void;

// ============================================================================
// SERVICE CLASS
// ============================================================================

class EmployeesService {
  private db: Firestore;
  private app: FirebaseApp;
  private listeners: Map<string, Unsubscribe> = new Map();
  private employeeCache: Map<string, Employee> = new Map();
  private _allEmployeesCache: { data: Employee[]; timestamp: number } | null = null;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.app = initializeApp(firebaseConfig, 'employeesFirestore');
    this.db = getFirestore(this.app);
  }

  /**
   * Convert Firestore data to Employee format
   */
  private convertFirestoreToEmployee(id: string, data: DocumentData): Employee {
    return {
      id,
      name: data.name || data.fullName || 'Unknown Employee',
      email: data.email || '',
      department: data.department || '',
      position: data.position || data.jobTitle || '',
      designation: data.designation || '',
      image: data.image || data.profileImage || data.userImage || '',
      phone: data.phone || data.phoneNumber || '',
      isActive: data.isActive !== false,
      joinDate: data.joinDate || data.createdAt || '',
      lastActive: data.lastActive || data.updatedAt || ''
    };
  }

  /**
   * Convert base64 image to data URL
   */
  private formatImageUrl(base64Image: string): string {
    if (!base64Image) return '';
    
    if (base64Image.startsWith('data:image/')) {
      return base64Image;
    }
    
    if (base64Image.startsWith('/9j/') || base64Image.startsWith('iVBORw0KGgo')) {
      return `data:image/jpeg;base64,${base64Image}`;
    }
    
    return base64Image;
  }

  /**
   * Get all employees
   */
  async getAllEmployees(): Promise<Employee[]> {
    try {
      // Check cache
      if (this._allEmployeesCache && Date.now() - this._allEmployeesCache.timestamp < this.CACHE_TTL) {
        return this._allEmployeesCache.data;
      }

      const employeesCollection = collection(this.db, 'employees');
      const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(employeesCollection);
      const employees: Employee[] = [];

      querySnapshot.forEach((doc: DocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data) {
          const employee = this.convertFirestoreToEmployee(doc.id, data);
          if (employee.image) {
            employee.image = this.formatImageUrl(employee.image);
          }
          employees.push(employee);
          this.employeeCache.set(doc.id, employee);
        }
      });

      this._allEmployeesCache = { data: employees, timestamp: Date.now() };

      return employees;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get employee by ID
   */
  async getEmployeeById(employeeId: string): Promise<Employee | null> {
    try {
      if (this.employeeCache.has(employeeId)) {
        return this.employeeCache.get(employeeId)!;
      }

      const docRef = doc(this.db, 'employees', employeeId);
      const docSnap: DocumentSnapshot<DocumentData> = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const employee = this.convertFirestoreToEmployee(employeeId, data);
        
        if (employee.image) {
          employee.image = this.formatImageUrl(employee.image);
        }
        
        this.employeeCache.set(employeeId, employee);
        
        return employee;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get multiple employees by IDs
   */
  async getEmployeesByIds(employeeIds: string[]): Promise<Employee[]> {
    try {
      const employees: Employee[] = [];
      const employeePromises = employeeIds.map(id => this.getEmployeeById(id));
      const results = await Promise.all(employeePromises);
      
      results.forEach(employee => {
        if (employee) {
          employees.push(employee);
        }
      });
      
      return employees;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Group employees by designation
   */
  groupEmployeesByDesignation(employees: Employee[]): {
    specialGroups: Record<string, Employee[]>;
    generalEmployees: Employee[];
    minGroupSize: number;
  } {
    const minGroupSize = 4;
    const designationGroups: Record<string, Employee[]> = {};
    const generalEmployees: Employee[] = [];

    employees.forEach(employee => {
      if (employee.designation && isSpecialDesignation(employee.designation)) {
        if (!designationGroups[employee.designation]) {
          designationGroups[employee.designation] = [];
        }
        designationGroups[employee.designation].push(employee);
      } else {
        generalEmployees.push(employee);
      }
    });

    const specialGroups: Record<string, Employee[]> = {};
    Object.entries(designationGroups).forEach(([designation, employeeList]) => {
      if (employeeList.length >= minGroupSize) {
        specialGroups[designation] = employeeList;
      } else {
        generalEmployees.push(...employeeList);
      }
    });

    return { specialGroups, generalEmployees, minGroupSize };
  }

  /**
   * Subscribe to real-time employee updates
   */
  subscribeToEmployees(callback: EmployeesCallback): Unsubscribe {
    const employeesCollection = collection(this.db, 'employees');
    const listenerId = Math.random().toString(36).substr(2, 9);

    const unsubscribe: Unsubscribe = onSnapshot(
      employeesCollection,
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        try {
          const employees: Employee[] = [];

          querySnapshot.forEach((doc: DocumentSnapshot<DocumentData>) => {
            const data = doc.data();
            if (data) {
              const employee = this.convertFirestoreToEmployee(doc.id, data);
              
              if (employee.image) {
                employee.image = this.formatImageUrl(employee.image);
              }
              
              employees.push(employee);
              this.employeeCache.set(doc.id, employee);
            }
          });

          this._allEmployeesCache = { data: employees, timestamp: Date.now() };
          callback(employees);
        } catch {
          // Silent error handling
        }
      },
      () => {
        // Silent error handling
      }
    );

    this.listeners.set(listenerId, unsubscribe);
    return unsubscribe;
  }

  /**
   * Get employee basic info for map display
   */
  async getEmployeeMapInfo(employeeId: string): Promise<{
    id: string;
    name: string;
    image?: string;
    initials: string;
    designation?: string;
  } | null> {
    try {
      const employee = await this.getEmployeeById(employeeId);
      
      if (!employee) return null;

      const initials = employee.name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');

      return {
        id: employee.id,
        name: employee.name,
        image: employee.image,
        initials: initials || '??',
        designation: employee.designation
      };
    } catch {
      return null;
    }
  }

  /**
   * Search employees by name
   */
  async searchEmployeesByName(searchTerm: string): Promise<Employee[]> {
    const allEmployees = await this.getAllEmployees();
    const searchLower = searchTerm.toLowerCase();
    
    return allEmployees.filter(employee => 
      employee.name.toLowerCase().includes(searchLower) ||
      (employee.email && employee.email.toLowerCase().includes(searchLower)) ||
      (employee.designation && employee.designation.toLowerCase().includes(searchLower))
    );
  }

  /**
   * Get employees statistics
   */
  async getEmployeesStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byDepartment: Record<string, number>;
    byDesignation: Record<string, number>;
    specialDesignations: number;
  }> {
    const employees = await this.getAllEmployees();
    
    return {
      total: employees.length,
      active: employees.filter(emp => emp.isActive).length,
      inactive: employees.filter(emp => !emp.isActive).length,
      byDepartment: employees.reduce((acc, employee) => {
        const dept = employee.department || 'Unknown';
        acc[dept] = (acc[dept] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byDesignation: employees.reduce((acc, employee) => {
        const designation = employee.designation || 'General Employee';
        acc[designation] = (acc[designation] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      specialDesignations: employees.filter(emp => isSpecialDesignation(emp.designation)).length
    };
  }

  /**
   * Clear employee cache
   */
  clearCache(): void {
    this.employeeCache.clear();
    this._allEmployeesCache = null;
  }

  /**
   * Clean up all listeners
   */
  cleanup(): void {
    this.listeners.forEach((unsubscribe: Unsubscribe) => unsubscribe());
    this.listeners.clear();
    this.clearCache();
  }
}

export const employeesService = new EmployeesService();
export default employeesService;